import React, { useState } from 'react';
import { Trans } from '@ui/i18n/trans';
import { useAvatars, Avatar, AvatarGroup } from '@app/admin/avatars/use-avatars';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@common/http/query-client';
import { toast } from '@ui/toast/toast';
import { CheckIcon } from '@ui/icons/material/Check';
import { ArrowBackIcon } from '@ui/icons/material/ArrowBack';
import { IconButton } from '@ui/buttons/icon-button';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { CloseIcon } from '@ui/icons/material/Close';

interface ChangeAvatarDialogProps {
    profileId: number;
    currentAvatarId?: number | null;
    onClose: () => void;
}

export function ChangeAvatarDialog({ profileId, currentAvatarId, onClose }: ChangeAvatarDialogProps) {
    const { data: groups, isLoading } = useAvatars();
    const queryClient = useQueryClient();
    const [selectedId, setSelectedId] = useState<number | null>(currentAvatarId ?? null);

    const updateProfile = useMutation({
        mutationFn: (avatarId: number) =>
            apiClient.put(`user/profiles/${profileId}`, { avatar_id: avatarId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user', 'profiles'] });
            toast('Avatar atualizado!');
            // Don't close - stay on avatar selection screen
        },
        onError: () => toast.danger('Erro ao atualizar avatar'),
    });

    const handleSelect = (avatar: Avatar) => {
        setSelectedId(avatar.id);
        updateProfile.mutate(avatar.id);
    };

    return (
        <div className="fixed inset-0 z-50 bg-background flex flex-col w-full max-w-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-16 border-b border-white/10">
                <div className="flex items-center gap-12">
                    <IconButton onClick={onClose}>
                        <ArrowBackIcon />
                    </IconButton>
                    <h2 className="text-xl font-semibold">
                        <Trans message="Escolha Seu Avatar" />
                    </h2>
                </div>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-16 md:px-24 md:py-16 flex flex-col items-center">
                {isLoading ? (
                    <div className="flex justify-center py-40">
                        <ProgressCircle isIndeterminate />
                    </div>
                ) : (
                    <div className="w-full">
                        {groups?.map((group) => (
                            <AvatarGroupSection
                                key={group.id}
                                group={group}
                                selectedId={selectedId}
                                onSelect={handleSelect}
                                isUpdating={updateProfile.isPending}
                            />
                        ))}
                        {(!groups || groups.length === 0) && (
                            <div className="text-center py-20 text-muted">
                                <Trans message="Nenhum avatar disponível." />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

interface AvatarGroupSectionProps {
    group: AvatarGroup;
    selectedId: number | null;
    onSelect: (avatar: Avatar) => void;
    isUpdating: boolean;
}

function AvatarGroupSection({
    group,
    selectedId,
    onSelect,
    isUpdating,
}: AvatarGroupSectionProps) {
    if (!group.avatars || group.avatars.length === 0) return null;

    return (
        <div className="mb-32 w-full max-w-full">
            <h3 className="text-lg font-medium text-white mb-16 px-4">{group.name}</h3>

            <div className="relative w-full">
                <style dangerouslySetInnerHTML={{
                    __html: `
                    .avatar-scroll-hidden::-webkit-scrollbar {
                        display: none !important;
                        -webkit-appearance: none !important;
                        width: 0 !important;
                        height: 0 !important;
                    }
                    .avatar-scroll-hidden {
                        -ms-overflow-style: none !important;
                        scrollbar-width: none !important;
                    }
                `}} />
                <div
                    className="flex overflow-x-auto snap-x snap-mandatory px-[10px] py-8 items-center avatar-scroll-hidden touch-pan-x"
                    style={{
                        WebkitOverflowScrolling: 'touch',
                        gap: '12px',
                        scrollPadding: '0 10px'
                    }}
                >
                    {group.avatars.map((avatar) => (
                        <div key={avatar.id} className="snap-start flex-shrink-0">
                            <button
                                onClick={() => !isUpdating && onSelect(avatar)}
                                disabled={isUpdating}
                                className={`
                                    relative w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-xl overflow-hidden
                                    transition-transform duration-200 hover:scale-105 focus:outline-none
                                    ${selectedId === avatar.id ? 'ring-4 ring-primary' : 'ring-2 ring-white/20 hover:ring-white/50'}
                                    ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                `}
                            >
                                <img
                                    src={avatar.image_url}
                                    alt={avatar.name || 'Avatar'}
                                    className="w-full h-full object-cover"
                                    draggable={false}
                                />
                                {selectedId === avatar.id && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                        <CheckIcon className="text-white w-32 h-32 drop-shadow-lg" />
                                    </div>
                                )}
                            </button>
                        </div>
                    ))}
                    {/* Ghost spacer to ensure right padding at end of scroll */}
                    <div className="flex-shrink-0 w-24 h-1 items-transparent" />
                </div>
            </div>
        </div>
    );
}
